import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "../store/authStore";
import {
  FiMenu,
  FiUser,
  FiPlusCircle,
  FiImage,
  FiShoppingCart,
  FiX,
  FiLogOut,
  FiGlobe,
  FiLogIn,
} from "react-icons/fi";
import { FaCoins } from "react-icons/fa";

// Define available languages (could be moved to a config file)
const languages = [
  { code: "en", name: "English" },
  { code: "es", name: "Español" },
  { code: "fr", name: "Français" },
  { code: "de", name: "Deutsch" },
  { code: "it", name: "Italiano" },
  { code: "pt", name: "Português" },
  { code: "ja", name: "日本語" },
  { code: "ko", name: "한국어" },
  { code: "hi", name: "हिन्दी" },
  { code: "id", name: "Bahasa Indonesia" },
  { code: "vi", name: "Tiếng Việt" },
  { code: "th", name: "ไทย" },
  { code: "pl", name: "Polski" },
  { code: "nl", name: "Nederlands" },
  { code: "sv", name: "Svenska" },
  { code: "no", name: "Norsk" },
  { code: "fi", name: "Suomi" },
  { code: "el", name: "Ελληνικά" },
  // Add other languages from /public/locales if needed
];

interface HeaderProps {
  onMenuClick?: () => void;
}

const Header: React.FC<HeaderProps> = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [desktopMenuOpen, setDesktopMenuOpen] = useState(false);
  const desktopMenuRef = useRef<HTMLDivElement>(null);
  const credits = user ? user.free_credits + user.paid_credits : 0;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Close mobile menu if clicked outside
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        const menuButton = document.getElementById("mobile-menu-button");
        if (!menuButton || !menuButton.contains(event.target as Node)) {
          setMenuOpen(false);
        }
      }
      // Close desktop menu if clicked outside
      if (
        desktopMenuRef.current &&
        !desktopMenuRef.current.contains(event.target as Node)
      ) {
        setDesktopMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle user logout
  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  // Toggle navigation menu
  const toggleMenu = () => {
    setMenuOpen((prev) => !prev);
  };

  // Toggle desktop user menu
  const toggleDesktopMenu = () => {
    setDesktopMenuOpen((prev) => !prev);
  };

  // Handle language change
  const handleChangeLanguage = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const lang = event.target.value;
    i18n.changeLanguage(lang);
    setMenuOpen(false); // Close mobile menu if open
  };
  console.log(user);

  return (
    <header className="bg-black bg-opacity-90 backdrop-blur-sm text-white p-4 sticky top-0 z-50 border-b border-gray-800">
      <div className="container mx-auto flex justify-between items-center">
        {/* Left Section: Logo and Desktop Credits */}
        <div className="flex items-center space-x-4">
          <Link to="/" className="text-2xl font-bold tracking-wider text-white">
            <img
              src="/genflow.png"
              alt={t("appName")}
              className="h-8 w-32 mr-2"
            />
          </Link>
          {/* Display Credits - Desktop */}
          {isAuthenticated && credits != null && (
            <div className="hidden md:flex items-center text-sm ml-6">
              <FaCoins className="h-5 w-5 text-yellow-400 inline-block mr-1" />
              <span>{t("header.credits", { count: credits })}</span>
            </div>
          )}
        </div>

        {/* Middle Section: Navigation Links - Desktop */}
        {isAuthenticated && (
          <nav className="hidden md:flex space-x-6">
            <Link
              to="/home"
              className="text-white hover:text-gray-300 transition-colors flex items-center space-x-1"
            >
              <FiPlusCircle className="h-5 w-5" />
              <span>{t("header.create")}</span>
            </Link>
            <Link
              to="/my-images"
              className="text-white hover:text-gray-300 transition-colors flex items-center space-x-1"
            >
              <FiImage className="h-5 w-5" />
              <span>{t("header.myImages")}</span>
            </Link>
          </nav>
        )}

        {/* Right Section: User/Auth Controls & Language Selector */}
        <div className="flex items-center space-x-2 md:space-x-4">
          {/* Language Selector - Desktop */}
          <div className="hidden md:block relative">
            <FiGlobe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <select
              value={i18n.language.split("-")[0]} // Use current language, handle variations like en-US
              onChange={handleChangeLanguage}
              className="appearance-none bg-gray-800 text-white text-sm rounded-full pl-9 pr-4 py-1.5 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors cursor-pointer"
              aria-label={t("header.selectLanguageAriaLabel")}
            >
              {languages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </select>
          </div>

          {/* User Account Button - Desktop */}
          {isAuthenticated && (
            <div className="relative hidden md:block" ref={desktopMenuRef}>
              {/* Hide on mobile */}
              <button
                className="flex items-center space-x-2 px-3 py-1.5 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors"
                onClick={toggleDesktopMenu}
              >
                <FiUser className="h-5 w-5" />
              </button>
              {/* Desktop Dropdown Menu */}
              {desktopMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-50 py-2">
                  {!user?.is_anonymous && (
                    <div className="px-4 py-2 text-sm text-gray-300 border-b border-gray-700">
                      <>
                        {t("header.signedInAs")}
                        <br />
                        <span className="font-medium text-white break-all">
                          {user?.email || user?.phone}
                        </span>
                      </>
                    </div>
                  )}
                  {credits != null && (
                    <div className="px-4 py-2 text-sm text-gray-300 border-b border-gray-700 flex items-center space-x-2">
                      <FaCoins className="h-5 w-5 text-yellow-400" />
                      <span>{t("header.credits", { count: credits })}</span>
                    </div>
                  )}
                  <Link
                    to="/home"
                    className="flex items-center space-x-2 px-4 py-2 text-sm text-white hover:bg-gray-700 rounded"
                    onClick={() => setDesktopMenuOpen(false)}
                  >
                    <FiPlusCircle className="h-5 w-5" />
                    <span>{t("header.create")}</span>
                  </Link>
                  <Link
                    to="/my-images"
                    className="flex items-center space-x-2 px-4 py-2 text-sm text-white hover:bg-gray-700 rounded"
                    onClick={() => setDesktopMenuOpen(false)}
                  >
                    <FiImage className="h-5 w-5" />
                    <span>{t("header.myImages")}</span>
                  </Link>
                  <Link
                    to="/purchase"
                    className="flex items-center space-x-2 px-4 py-2 text-sm text-white hover:bg-gray-700 rounded"
                    onClick={() => setDesktopMenuOpen(false)}
                  >
                    <FiShoppingCart className="h-5 w-5" />
                    <span>{t("header.buyCredits")}</span>
                  </Link>
                  {user?.is_anonymous ? (
                    <Link
                      to="/login"
                      className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-white hover:bg-gray-700 rounded text-left"
                      onClick={() => setDesktopMenuOpen(false)}
                    >
                      <FiLogIn className="h-5 w-5" />
                      <span>{t("header.signIn")}</span>
                    </Link>
                  ) : (
                    <button
                      onClick={async () => {
                        await handleLogout();
                        setDesktopMenuOpen(false);
                      }}
                      className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-white hover:bg-gray-700 rounded text-left"
                    >
                      <FiLogOut className="h-5 w-5" />
                      <span>{t("header.signOut")}</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            {" "}
            {/* Show only on mobile */}
            <button
              id="mobile-menu-button"
              className="text-white hover:text-gray-400 p-1"
              style={{
                backgroundColor: "transparent",
              }}
              onClick={toggleMenu}
              aria-label={t("header.menuAriaLabel")}
            >
              <FiMenu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Side Menu Overlay */}
      {menuOpen && (
        <div
          className="fixed inset-0 bg-black z-40 md:hidden"
          onClick={toggleMenu} // Close menu when overlay is clicked
          aria-hidden="true"
        ></div>
      )}

      {/* Mobile Side Menu */}
      <div
        ref={menuRef}
        style={{
          transform: menuOpen ? "translateX(0)" : "translateX(120%)",
        }}
        className={`fixed top-0 right-0 h-full w-full bg-gray-800 shadow-lg z-50 transform transition-transform duration-300 ease-in-out md:hidden`}
      >
        <div className="p-4 bg-gray-800">
          {/* Close Button */}
          <div className="flex justify-end mb-4">
            <button
              onClick={toggleMenu}
              className="text-gray-400 hover:text-white"
              aria-label={t("header.closeMenuAriaLabel")}
            >
              <FiX className="h-6 w-6" />
            </button>
          </div>

          {/* Menu Content */}
          <nav className="flex flex-col space-y-4">
            {/* Language Selector - Mobile */}
            <div className="px-2 py-2">
              <label htmlFor="mobile-lang-select" className="sr-only">
                {t("header.selectLanguageAriaLabel")}
              </label>
              <div className="relative">
                <FiGlobe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <select
                  id="mobile-lang-select"
                  value={i18n.language.split("-")[0]} // Use current language
                  onChange={handleChangeLanguage}
                  className="w-full appearance-none bg-gray-700 text-white text-sm rounded-md pl-9 pr-4 py-2 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors cursor-pointer"
                >
                  {languages.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {isAuthenticated && (
              <>
                {/* User Info */}
                {!user?.is_anonymous && (
                  <div className="px-2 py-2 text-sm text-gray-300 border-b border-gray-700">
                    <>
                      {t("header.signedInAs")}
                      <br />
                      <span className="font-medium text-white break-all">
                        {user?.email}
                      </span>
                    </>
                  </div>
                )}
                {/* Credits */}
                {credits != null && (
                  <div className="px-2 py-2 text-sm text-gray-300 border-b border-gray-700 flex items-center space-x-2">
                    <FaCoins className="h-5 w-5 text-yellow-400" />
                    <span>{t("header.credits", { count: credits })}</span>
                  </div>
                )}
                {/* Navigation Links */}
                <Link
                  to="/home"
                  className="flex items-center space-x-2 px-2 py-2 text-sm text-white hover:bg-gray-700 rounded"
                  onClick={toggleMenu} // Close menu on navigation
                >
                  <FiPlusCircle className="h-5 w-5" />
                  <span>{t("header.create")}</span>
                </Link>
                <Link
                  to="/my-images"
                  className="flex items-center space-x-2 px-2 py-2 text-sm text-white hover:bg-gray-700 rounded"
                  onClick={toggleMenu}
                >
                  <FiImage className="h-5 w-5" />
                  <span>{t("header.myImages")}</span>
                </Link>
                <Link
                  to="/purchase"
                  className="flex items-center space-x-2 px-2 py-2 text-sm text-white hover:bg-gray-700 rounded"
                  onClick={toggleMenu}
                >
                  <FiShoppingCart className="h-5 w-5" />
                  <span>{t("header.buyCredits")}</span>
                </Link>
                {user?.is_anonymous ? (
                  <Link
                    to="/login"
                    className="w-full flex items-center space-x-2 px-2 py-2 text-sm text-white hover:bg-gray-700 rounded text-left"
                    onClick={toggleMenu}
                  >
                    <FiLogIn className="h-5 w-5" />
                    <span>{t("header.signIn")}</span>
                  </Link>
                ) : (
                  <button
                    onClick={() => {
                      handleLogout();
                      toggleMenu();
                    }}
                    className="w-full flex items-center space-x-2 px-2 py-2 text-sm text-white hover:bg-gray-700 rounded text-left"
                  >
                    <FiLogOut className="h-5 w-5" />
                    <span>{t("header.signOut")}</span>
                  </button>
                )}
              </>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
